import { z } from 'zod';
import { parseClassResponse } from '../temperature/handling-class.repository';
import { customerBillingProfileSchema } from './billing-profile.repository';
import { apiRequest } from '../masterdata/repository';
import { uploadWorkbook } from '../operations/files.repository';
import type { Action, Row, Value } from './types';

const valueSchema: z.ZodType<Value> = z.lazy(() =>
  z.union([
    z.string(),
    z.number().finite(),
    z.boolean(),
    z.null(),
    z.array(valueSchema),
    z.record(valueSchema),
  ])
);
export function workflowValue(value: unknown): Value {
  return valueSchema.parse(value);
}
export function object(value: Value | undefined): Row {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value : {};
}
export function at(data: Value | undefined, path: string): Value | undefined {
  return path.split('.').reduce<Value | undefined>((value, key) => object(value)[key], data);
}
export function text(value: Value | undefined): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}
export function route(template: string, data: Row): string {
  return template.replace(/\{([\w.]+)\}/g, (_match: string, key: string) => {
    const value = text(at(data, key));
    if (!value) throw new Error(`Select ${key.replaceAll('_', ' ')} to continue.`);
    return encodeURIComponent(value);
  });
}
export function rows(value: Value | undefined, collection?: string): Row[] {
  const list = collection ? at(value, collection) : value;
  if (!Array.isArray(list))
    throw new Error('The server returned an unexpected list. Refresh or contact support.');
  return list.map((entry) => {
    if (entry === null || typeof entry !== 'object' || Array.isArray(entry))
      throw new Error('The server returned an unexpected record.');
    return entry;
  });
}
export async function read(path: string, signal?: AbortSignal): Promise<Value> {
  const response = parseClassResponse(path, await apiRequest(path, 'GET', undefined, signal));
  return valueSchema.parse(
    path.split('?')[0] === '/admin/masterdata/customer-billing-profiles'
      ? customerBillingProfileSchema.array().parse(response)
      : response
  );
}
export async function execute(action: Action, data: Row, values: Row, file?: File): Promise<Value> {
  const prepared = action.prepare ? await action.prepare(data, values) : values;
  const payload = { ...prepared, ...action.fixed };
  const path = route(action.path, { ...data, ...values });
  if (action.upload && !action.upload.path && file) {
    return valueSchema.parse(
      await uploadWorkbook(
        path,
        file,
        Object.fromEntries(
          Object.entries(payload)
            .filter(([, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => [key, typeof value === 'string' ? value : JSON.stringify(value)])
        )
      )
    );
  }
  if (action.method === 'GET') {
    const query = new URLSearchParams(
      Object.entries(payload)
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)])
    );
    return read(`${path}${query.size ? `${path.includes('?') ? '&' : '?'}${query}` : ''}`);
  }
  return valueSchema.parse(await apiRequest(path, action.method ?? 'POST', payload));
}
export async function uploadAttachment(path: string, file: File): Promise<Row> {
  return object(valueSchema.parse(await uploadWorkbook(path, file)));
}
